import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SvgCalendar from "../components/icons/CalendarMonth";
import SvgNavigateBefore from "../components/icons/NavigateBefore";
import SvgNavigateNext from "../components/icons/NagivateNext";
import Icon from "../components/ui/Icon";
import { useI18n } from "../hooks/useTranslations";
import { formatDate, isMobile } from "../types/utils";
import styles from "./DatePicker.module.css";
import {
  addDays,
  addMonths,
  firstOfMonth,
  getFirstDayOfWeekForLocale,
  getMonthNames,
  getWeekdayLabels,
  toDateInput,
  type WeekdayIndex,
} from "./datePickerUtils";
import { useFloatingPanel } from "./useFloatingPanel";

export const getMonthGrid = (viewMonth: string, firstDayOfWeek: WeekdayIndex): string[] => {
  const first = firstOfMonth(viewMonth);
  const [year = 0, month = 1, day = 1] = first.split("-").map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  const leadingDays = (weekday - firstDayOfWeek + 7) % 7;
  const start = addDays(first, -leadingDays);

  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
};

export type DatePickerProps = {
  value?: string | null;
  defaultValue?: string | null;
  onChange: (value: string | null) => void;
  clearable?: boolean;
  disabled?: boolean;
  isInvalid?: boolean;
  noBorder?: boolean;
};

const YEAR_RANGE_PAST = 100;
const YEAR_RANGE_FUTURE = 10;

export function DatePicker({
  value,
  defaultValue,
  onChange,
  clearable = false,
  disabled = false,
  isInvalid = false,
  noBorder = false,
}: DatePickerProps) {
  const i18n = useI18n();
  const labels = i18n.t.common.datePicker;
  const locale = i18n.language;
  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? "");
  const currentValue = (isControlled ? value : uncontrolledValue) ?? "";

  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => firstOfMonth(currentValue || toDateInput(new Date())));
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const panelStyle = useFloatingPanel(isOpen, () => setIsOpen(false), containerRef, panelRef);

  useEffect(() => {
    if (isOpen) {
      setViewMonth(firstOfMonth(currentValue || toDateInput(new Date())));
    }
  }, [currentValue, isOpen]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const today = toDateInput(new Date());
  const [viewYear = 0, viewMonthNumber = 1] = viewMonth.split("-").map(Number);
  const currentYear = new Date().getFullYear();
  const resolvedFirstDayOfWeek = getFirstDayOfWeekForLocale(locale);
  const monthNames = getMonthNames(locale);
  const weekdayLabels = getWeekdayLabels(locale, resolvedFirstDayOfWeek);
  const days = getMonthGrid(viewMonth, resolvedFirstDayOfWeek);
  const years = Array.from(
    { length: YEAR_RANGE_PAST + YEAR_RANGE_FUTURE + 1 },
    (_, index) => currentYear - YEAR_RANGE_PAST + index,
  );
  const applyChange = (nextValue: string | null) => {
    if (!isControlled) {
      setUncontrolledValue(nextValue ?? "");
    }

    onChange(nextValue);
  };

  if (isMobile) {
    return (
      <div className={styles.wrap}>
        <div className={styles.mobileRow}>
          <input
            type="date"
            className={classNames(styles.mobileInput, {
              [styles.mobileInputDisabled]: disabled,
              [styles.mobileInputInvalid]: isInvalid,
            })}
            value={currentValue}
            disabled={disabled}
            onChange={(event) => applyChange(event.target.value || null)}
          />
          {clearable && currentValue && (
            <button type="button" className={styles.clearButton} disabled={disabled} onClick={() => applyChange(null)}>
              {labels.clearButton}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrap} ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        className={classNames(styles.trigger, {
          [styles.triggerDisabled]: disabled,
          [styles.triggerInvalid]: isInvalid,
          [styles.triggerNoBorder]: noBorder,
        })}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className={classNames(styles.value, !currentValue && styles.placeholder)}>
          {currentValue ? formatDate(currentValue, locale) : labels.placeholder}
        </span>
        <span className={styles.indicatorSeparator} />
        <Icon glyph={SvgCalendar} className={styles.caret} />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            className={styles.panel}
            style={
              panelStyle ?? {
                position: "fixed",
                top: 0,
                left: 0,
                visibility: "hidden",
              }
            }
          >
            <div className={styles.panelHeader}>
              <button
                type="button"
                className={styles.navButton}
                onClick={() => setViewMonth((current) => addMonths(current, -1))}
                aria-label={labels.previousMonth}
              >
                <Icon glyph={SvgNavigateBefore} className={styles.navIcon} />
              </button>

              <div className={styles.monthYearSelect}>
                <select
                  aria-label={labels.selectMonth}
                  className={styles.select}
                  value={String(viewMonthNumber)}
                  onChange={(event) => setViewMonth(`${viewYear}-${event.target.value.padStart(2, "0")}-01`)}
                >
                  {monthNames.map((name, index) => (
                    <option key={name} value={String(index + 1)}>
                      {name}
                    </option>
                  ))}
                </select>

                <select
                  aria-label={labels.selectYear}
                  className={styles.select}
                  value={String(viewYear)}
                  onChange={(event) =>
                    setViewMonth(`${event.target.value}-${String(viewMonthNumber).padStart(2, "0")}-01`)
                  }
                >
                  {years.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                className={styles.navButton}
                onClick={() => setViewMonth((current) => addMonths(current, 1))}
                aria-label={labels.nextMonth}
              >
                <Icon glyph={SvgNavigateNext} className={styles.navIcon} />
              </button>
            </div>

            <div className={styles.weekdays}>
              {weekdayLabels.map((weekdayLabel) => (
                <span key={weekdayLabel} className={styles.weekdayLabel}>
                  {weekdayLabel}
                </span>
              ))}
            </div>

            <div className={styles.days}>
              {days.map((day) => {
                const dayMonth = Number(day.slice(5, 7));
                const dayNumber = Number(day.slice(8, 10));
                return (
                  <button
                    key={day}
                    type="button"
                    className={classNames(styles.day, {
                      [styles.dayOutside]: dayMonth !== viewMonthNumber,
                      [styles.dayToday]: day === today,
                      [styles.daySelected]: day === currentValue,
                    })}
                    onClick={() => {
                      applyChange(day);
                      setIsOpen(false);
                    }}
                  >
                    {dayNumber}
                  </button>
                );
              })}
            </div>

            {clearable && currentValue && (
              <button
                type="button"
                className={styles.clearButton}
                onClick={() => {
                  applyChange(null);
                  setIsOpen(false);
                }}
              >
                {labels.clearButton}
              </button>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

export default DatePicker;
